// GeexRankedStack — Geex frame 07 (workspace/design-refs/geex/
// 06-analytics-tonal-bars.png, "My Daily Spendings" — the paired frame
// ships in the same source PNG as GeexTonalBars/06 on the geex share
// grid): stacked blocks of decreasing width, white-to-hue tonal steps,
// giant % + label per block, one money-figure footer row. No axes — the
// block-width rhythm alone carries "biggest share first".
//
// items: [{ label, value, amount? }] — value is the percent number (58,
// not '58%'); amount is an optional pre-formatted footer figure
// ('$2,281.50'). Footer row only renders when at least one item has an
// amount.
// ink: base foreground (default 'title'). hue: optional — pale-to-base
// hue tonal steps instead of ink-alpha (poster context).

// FA204 / frank trap — helper lives in `scope`, not module scope (a bare
// module-scope const reads as undefined post-frank.toJSON; the Stack's
// children factory died while the helper-free Footer still rendered).
export const GeexRankedStack = {
  scope: {
    blockTone: (ink, hue, i) => {
      if (hue) {
        const steps = [40, 25, 12, 0]
        const step = steps[Math.min(i, steps.length - 1)]
        return step ? `${hue}+${step}` : hue
      }
      const alphas = [5, 14, 24, 36]
      return `${ink}.${alphas[Math.min(i, alphas.length - 1)]}`
    }
  },
  flow: 'y',
  gap: 'V',
  width: '100%',
  items: [],
  ink: 'title',

  Stack: {
    flow: 'y',
    gap: 'V',
    childrenAs: 'state',
    children: (el, s) => {
      const parent = el.parent
      const items = parent.items || []
      const n = items.length || 1
      const ink = parent.ink || 'title'
      const hue = parent.hue
      return items.map((it, i) => ({
        key: it.label || `block${i}`,
        pct: Math.max(100 - i * (40 / Math.max(n - 1, 1)), 50),
        tone: el.scope.blockTone(ink, hue, i),
        value: it.value,
        label: it.label || '',
        ink
      }))
    },
    childExtends: {
      round: 'radiusCard',
      padding: 'C B',
      width: (el, s) => `${s.pct}%`,
      background: (el, s) => s.tone,
      transition: 'A defaultBezier width, A defaultBezier background',

      Value: {
        tag: 'span',
        display: 'block',
        fontSize: 'G',
        fontWeight: '700',
        fontFamily: 'Code',
        lineHeight: '0.95',
        color: (el, s) => s.ink,
        text: (el, s) => `${s.value ?? 0}%`
      },
      Label: {
        tag: 'span',
        display: 'block',
        fontSize: 'Z',
        opacity: '0.55',
        color: (el, s) => s.ink,
        // `items[].label` is a KEY-IN-STATE carrier: a consumer may hand this
        // chart a bundle key (workspace/pages/admin/usage.js feeds
        // `admin.usage.attr.systemEmpty`). Resolved HERE, at the render site —
        // `translate()` returns its input unchanged when nothing resolves, so a
        // tenant-supplied label passes through untouched.
        text: (el, s) => el.call('polyglot', s.label || '')
      }
    }
  },

  Footer: {
    flow: 'x',
    align: 'baseline space-between',
    gap: 'B',
    hide: (el) => !(el.parent.items || []).some((it) => it.amount),
    childrenAs: 'state',
    children: (el, s) => {
      const parent = el.parent
      const ink = parent.ink || 'title'
      return (parent.items || []).map((it, i) => ({
        key: it.label || `foot${i}`,
        label: it.label || '',
        amount: it.amount || '',
        ink
      }))
    },
    childExtends: {
      flow: 'y',
      gap: 'X',
      Label: {
        tag: 'span',
        fontSize: 'Z',
        opacity: '0.45',
        color: (el, s) => s.ink,
        text: (el, s) => el.call('polyglot', s.label || '')
      },
      Amount: {
        tag: 'span',
        fontSize: 'Z',
        fontWeight: '700',
        fontFamily: 'Code',
        color: (el, s) => s.ink,
        text: (el, s) => s.amount
      }
    }
  }
}
